import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ORDERWISE_OUTPUT_MAPPER_KEYS } from './constants.ts';
import { GET_ORDERWISE_INTEGRATION_PARAMETER_DEFINITIONS } from './queries.ts';
import type { OrderwiseFormData, OrderwiseIntegration, TabType } from './types.ts';
import { FORM_DEFAULT_VALUES } from './types.ts';
import CheckboxGroup from '../../../core/components/CheckboxGroup.tsx';
import DecimalField from '../../../core/components/Fields/DecimalField.tsx';
import JsonField from '../../../core/components/Fields/JsonField.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import { SecretField } from '../../../core/components/Secret.tsx';
import { OutputMapperEditorPanel } from '../../components/OutputMapper/index.ts';

const Tab = ({ label, isActive, onClick }: { label: string; isActive: boolean; onClick: () => void }) => (
    <button
        type="button"
        onClick={onClick}
        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            isActive
                ? 'border-accent-primary text-accent-primary'
                : 'border-transparent text-content-weak hover:text-content hover:border-border-strong'
        }`}
    >
        {label}
    </button>
);

export interface OrderwiseIntegrationFormProps {
    integration?: OrderwiseIntegration;
    onSubmit: (
        data: OrderwiseFormData,
        onSuccess?: (updatedIntegration: OrderwiseIntegration) => void
    ) => Promise<void>;
    formId?: string;
}

/**
 * Shared Orderwise integration form: main tabs (configuration / order mapper / RFQ mapper) and mapper sub-tabs.
 */
const OrderwiseIntegrationForm = ({
    integration,
    onSubmit,
    formId = 'orderwise-integration-form',
}: OrderwiseIntegrationFormProps) => {
    const { t } = useTranslation('integrations');

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    const [activeTab, setActiveTab] = useState<TabType>('configuration');

    useEffect(() => {
        if (integration) {
            form.reset({
                ...FORM_DEFAULT_VALUES,
                name: integration.name,
                username: integration.username,
                password: integration.password ?? '',
                baseUrl: integration.baseUrl,
                sessionId: integration.sessionId,
                attachmentsBaseUrl: integration.attachmentsBaseUrl ?? '',
                priceCheckLowerThresholdPercentage: integration.priceCheckLowerThresholdPercentage ?? 0,
                priceCheckUpperThresholdPercentage: integration.priceCheckUpperThresholdPercentage ?? 0,
                integrationParameters: integration.integrationParameters ?? {},
                proxy: integration.proxy,
                fetchProgress: integration.fetchProgress,
                orderChannelIds: integration.orderChannels.map((channel) => channel.id),
                rfqChannelIds: integration.rfqChannels.map((channel) => channel.id),
            });
        }
    }, [integration, form]);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });

    const orderChannels = channelData?.orderProcessingChannels || [];
    const rfqChannels = channelData?.rfqProcessingChannels || [];

    const { data: parameterDefinitionsData } = useQuery(GET_ORDERWISE_INTEGRATION_PARAMETER_DEFINITIONS);
    const parameterDefinitions = parameterDefinitionsData?.orderwiseIntegrationParameterDefinitions || [];

    return (
        <div className="max-w-6xl w-full flex flex-col flex-1 min-h-0">
            <div className="flex border-b border-border mb-4">
                <Tab
                    label={t('orderwise.form.tabs.configuration')}
                    isActive={activeTab === 'configuration'}
                    onClick={() => setActiveTab('configuration')}
                />
                <Tab
                    label={t('orderwise.form.orderOutputMapperConfig')}
                    isActive={activeTab === 'order'}
                    onClick={() => setActiveTab('order')}
                />
                <Tab
                    label={t('orderwise.form.rfqOutputMapperConfig')}
                    isActive={activeTab === 'rfq'}
                    onClick={() => setActiveTab('rfq')}
                />
            </div>

            {activeTab === 'configuration' && (
                <form
                    className="flex flex-col gap-6 flex-1 min-h-0"
                    onSubmit={form.handleSubmit((data) => onSubmit(data))}
                    id={formId}
                >
                    <Controller
                        name="name"
                        control={form.control}
                        render={({ field }) => (
                            <FormField label={t('orderwise.form.name')} error={formErrors.name}>
                                <StringField {...field} />
                            </FormField>
                        )}
                    />

                    <div className="text-lg font-semibold mt-2">
                        {t('orderwise.form.header.integrationAuthentication')}
                    </div>

                    <Controller
                        name="username"
                        control={form.control}
                        render={({ field }) => (
                            <FormField label={t('orderwise.form.username')} error={formErrors.username}>
                                <StringField {...field} />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="password"
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={t('orderwise.form.password')}
                                error={formErrors.password}
                                helpText={t('secretUpdateOnly')}
                            >
                                <SecretField {...field} secretName="password" secretId={integration?.id} />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="baseUrl"
                        control={form.control}
                        render={({ field }) => (
                            <FormField label={t('orderwise.form.baseUrl')} error={formErrors.baseUrl}>
                                <StringField {...field} />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="sessionId"
                        control={form.control}
                        render={({ field }) => (
                            <FormField label={t('orderwise.form.sessionId')} error={formErrors.sessionId}>
                                <StringField {...field} />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="priceCheckLowerThresholdPercentage"
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={t('orderwise.form.priceCheckLowerThresholdPercentage')}
                                error={formErrors.priceCheckLowerThresholdPercentage}
                                helpText={t('orderwise.form.priceCheckLowerThresholdPercentageHelpText')}
                            >
                                <DecimalField {...field} value={field.value.toString()} decimalSeparator="," />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="priceCheckUpperThresholdPercentage"
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={t('orderwise.form.priceCheckUpperThresholdPercentage')}
                                error={formErrors.priceCheckUpperThresholdPercentage}
                                helpText={t('orderwise.form.priceCheckUpperThresholdPercentageHelpText')}
                            >
                                <DecimalField {...field} value={field.value.toString()} decimalSeparator="," />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="integrationParameters"
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={t('orderwise.form.integrationParameters')}
                                error={formErrors.integrationParameters}
                                helpText={
                                    <span>
                                        {t('orderwise.form.integrationParametersHelpText')}
                                        {parameterDefinitions.length > 0 && (
                                            <ul className="list-disc pl-4 mt-1">
                                                {parameterDefinitions.map((definition) => (
                                                    <li key={definition.key}>
                                                        <code>{definition.key}</code> ({definition.type}, default:{' '}
                                                        <code>{JSON.stringify(definition.default)}</code>) —{' '}
                                                        {definition.description}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </span>
                                }
                            >
                                <JsonField value={field.value} onValueChange={(value) => field.onChange(value)} />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="proxy"
                        control={form.control}
                        render={({ field }) => (
                            <FormField label={t('orderwise.form.proxy')} error={formErrors.proxy}>
                                <StringField {...field} />
                            </FormField>
                        )}
                    />

                    <Controller
                        name="attachmentsBaseUrl"
                        control={form.control}
                        render={({ field }) => (
                            <FormField
                                label={t('orderwise.form.attachmentsBaseUrl')}
                                error={formErrors.attachmentsBaseUrl}
                                helpText={t('orderwise.form.attachmentsBaseUrlHelpText')}
                            >
                                <StringField {...field} />
                            </FormField>
                        )}
                    />

                    <div className="text-lg font-semibold mt-2">{t('orderwise.form.header.integrationOrders')}</div>

                    <Controller
                        name="orderChannelIds"
                        control={form.control}
                        render={({ field }) => (
                            <FormField label={t('orderwise.form.orderChannelIds')} error={formErrors.orderChannelIds}>
                                <CheckboxGroup
                                    {...field}
                                    options={
                                        orderChannels.map((channel) => ({
                                            value: channel.id,
                                            label: channel.name,
                                        })) || []
                                    }
                                    onValueChange={(value) => field.onChange({ target: { value } })}
                                />
                            </FormField>
                        )}
                    />

                    <div className="text-lg font-semibold mt-2">{t('orderwise.form.header.integrationRFQs')}</div>

                    <Controller
                        name="rfqChannelIds"
                        control={form.control}
                        render={({ field }) => (
                            <FormField label={t('orderwise.form.rfqChannelIds')} error={formErrors.rfqChannelIds}>
                                <CheckboxGroup
                                    {...field}
                                    options={
                                        rfqChannels.map((channel) => ({
                                            value: channel.id,
                                            label: channel.name,
                                        })) || []
                                    }
                                    onValueChange={(value) => field.onChange({ target: { value } })}
                                />
                            </FormField>
                        )}
                    />
                </form>
            )}

            {activeTab === 'order' && (
                <OutputMapperEditorPanel
                    outputMapperKey={ORDERWISE_OUTPUT_MAPPER_KEYS.order}
                    ownerId={integration?.id}
                    recordType="order"
                />
            )}

            {activeTab === 'rfq' && (
                <OutputMapperEditorPanel
                    outputMapperKey={ORDERWISE_OUTPUT_MAPPER_KEYS.rfq}
                    ownerId={integration?.id}
                    recordType="rfq"
                />
            )}
        </div>
    );
};

export default OrderwiseIntegrationForm;
